/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Popover } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";

import {
  TagRoot,
  TagLabel,
  TagActionsWrapper,
  TagIconButton,
  SubGroupsContainer,
  OverflowBadge,
  EmptyLabel,
  PopoverContent,
} from "./styles";

const TAG_GAP = 8;
const OVERFLOW_BADGE_WIDTH = 40;

const RestaurantSubGroupTag = React.memo(
  React.forwardRef(
    ({ item, canEdit, canDelete, onEdit, onDelete, isMeasurement = false }, ref) => {
      return (
        <TagRoot
          ref={ref}
          aria-hidden={isMeasurement ? "true" : undefined}
          data-testid={isMeasurement ? undefined : `subgroup-tag-${item.id}`}
        >
          <TagLabel
            component="span"
            data-testid={isMeasurement ? undefined : `subgroup-tag-label-${item.id}`}
          >
            {item.name}
          </TagLabel>

          <TagActionsWrapper>
            {canEdit && (
              <TagIconButton
                size="small"
                onClick={() => onEdit?.(item)}
                data-testid={isMeasurement ? undefined : `subgroup-tag-edit-${item.id}`}
                aria-label={isMeasurement ? undefined : `Edit ${item.name}`}
                tabIndex={isMeasurement ? -1 : undefined}
              >
                <EditOutlinedIcon fontSize="inherit" />
              </TagIconButton>
            )}

            {canDelete && (
              <TagIconButton
                size="small"
                onClick={() => onDelete?.(item)}
                data-testid={isMeasurement ? undefined : `subgroup-tag-delete-${item.id}`}
                aria-label={isMeasurement ? undefined : `Delete ${item.name}`}
                tabIndex={isMeasurement ? -1 : undefined}
              >
                <CloseIcon fontSize="inherit" />
              </TagIconButton>
            )}
          </TagActionsWrapper>
        </TagRoot>
      );
    },
  ),
);

RestaurantSubGroupTag.displayName = "RestaurantSubGroupTag";

export const RenderRestaurantSubGroups = React.memo(
  ({
    data = [],
    canEdit = false,
    canDelete = false,
    onEdit,
    onDelete,
    emptyLabel = "No Restaurant Sub Groups",
  }) => {
    const containerRef = useRef(null);
    const measurementRefs = useRef(new Map());
    const hoverTimeout = useRef(null);

    const [visibleItems, setVisibleItems] = useState([]);
    const [hiddenItems, setHiddenItems] = useState([]);

    const [anchorEl, setAnchorEl] = useState(null);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const calculateVisibleItems = useCallback(() => {
      if (!containerRef.current || !data.length) return;

      const containerWidth = containerRef.current.offsetWidth;

      if (!containerWidth) {
        setVisibleItems([]);
        setHiddenItems(data);
        return;
      }

      let visibleCount = 0;
      let usedWidth = 0;

      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        const measuredElement = measurementRefs.current.get(item.id);

        if (!measuredElement) {
          return;
        }

        const itemWidth = Math.ceil(measuredElement.getBoundingClientRect().width);
        const gapWidth = visibleCount > 0 ? TAG_GAP : 0;
        const remainingItems = data.length - (i + 1);
        const overflowWidth = remainingItems > 0 ? TAG_GAP + OVERFLOW_BADGE_WIDTH : 0;
        const requiredWidth = usedWidth + gapWidth + itemWidth + overflowWidth;

        if (requiredWidth > containerWidth) {
          break;
        }

        usedWidth += gapWidth + itemWidth;
        visibleCount = i + 1;
      }

      setVisibleItems(data.slice(0, visibleCount));
      setHiddenItems(data.slice(visibleCount));
    }, [data]);

    useEffect(() => {
      calculateVisibleItems();

      if (!containerRef.current) return;

      const observer = new ResizeObserver(() => {
        calculateVisibleItems();
      });

      observer.observe(containerRef.current);

      return () => {
        observer.disconnect();
        clearTimeout(hoverTimeout.current);
      };
    }, [calculateVisibleItems]);

    const handleOpenPopover = (event) => {
      if (!hiddenItems.length) return;
      clearTimeout(hoverTimeout.current);
      setAnchorEl(event.currentTarget);
      setIsPopoverOpen(true);
    };

    const handleClosePopover = () => {
      hoverTimeout.current = setTimeout(() => {
        setAnchorEl(null);
        setIsPopoverOpen(false);
      }, 150);
    };

    const open = Boolean(anchorEl) && isPopoverOpen;

    if (!data.length) {
      return (
        <EmptyLabel data-testid="subgroups-empty-label">
          {emptyLabel}
        </EmptyLabel>
      );
    }

    return (
      <>
        <SubGroupsContainer
          ref={containerRef}
          data-testid="subgroups-container"
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              visibility: "hidden",
              pointerEvents: "none",
              height: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {data.map((item) => (
              <RestaurantSubGroupTag
                key={`measurement-${item.id}`}
                ref={(element) => {
                  if (element) {
                    measurementRefs.current.set(item.id, element);
                  } else {
                    measurementRefs.current.delete(item.id);
                  }
                }}
                item={item}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={onEdit}
                onDelete={onDelete}
                isMeasurement
              />
            ))}
          </Box>

          {visibleItems.map((item) => (
            <RestaurantSubGroupTag
              key={item.id}
              item={item}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}

          {hiddenItems.length > 0 && (
            <OverflowBadge
              onMouseEnter={handleOpenPopover}
              data-testid="subgroups-overflow-badge"
              aria-label={`${hiddenItems.length} more subgroups`}
            >
              +{hiddenItems.length}
            </OverflowBadge>
          )}
        </SubGroupsContainer>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          disableRestoreFocus
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              onMouseEnter: () => clearTimeout(hoverTimeout.current),
              onMouseLeave: handleClosePopover,
              sx: { p: 2, borderRadius: 2 },
              "data-testid": "subgroups-popover-paper",
            },
          }}
        >
          <PopoverContent data-testid="subgroups-popover-content">
            {hiddenItems.map((item) => (
              <RestaurantSubGroupTag
                key={item.id}
                item={item}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </PopoverContent>
        </Popover>
      </>
    );
  },
);

RenderRestaurantSubGroups.displayName = "RenderRestaurantSubGroups";
